import { type JSX } from 'react'
import { X } from 'lucide-react'
import { useAgentStore, type UserStatusOption } from '../../store/agentStore.js'
import { getSocket } from '../../socket/client.js'
import { cn } from '../../utils/cn.js'

interface BreakCodeDialogProps {
  visible: boolean
  onHide: () => void
}

export default function BreakCodeDialog({ visible, onHide }: BreakCodeDialogProps): JSX.Element {
  const breakCodes = useAgentStore((s) => s.breakCodes)
  const userStatus = useAgentStore((s) => s.userStatus)
  const setUserStatus = useAgentStore((s) => s.setUserStatus)

  const isOnBreak = !['login', 'available'].includes(userStatus.key)

  function handleSelectBreak(option: UserStatusOption): void {
    if (option.disabled) return
    setUserStatus(option)
    getSocket().emit('agent:status', { event: 'pause', reason: option.text })
    onHide()
  }

  function handleEndBreak(): void {
    setUserStatus({ key: 'available', text: 'Available' })
    getSocket().emit('agent:status', { event: 'unpause' })
    onHide()
  }

  // Close button only works when user is Available (not on break)
  function handleClose(): void {
    if (userStatus.key === 'available') {
      onHide()
    }
    // If on break, do nothing — dialog stays open
  }

  if (!visible) return <></>

  // Filter to show only break codes (not login/available)
  const breakCodeOptions = breakCodes.filter(
    (code) => !['login', 'available'].includes(code.key)
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      {/* Modal content wrapper — clicking outside (on the backdrop) does nothing */}
      <div className="relative w-full max-w-md rounded-[3px] bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {isOnBreak ? 'Change Break Status' : 'Select Break Code'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isOnBreak}
            title={isOnBreak ? 'You must end your break before closing' : 'Close'}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-[3px] transition-colors',
              isOnBreak
                ? 'cursor-not-allowed text-slate-300'
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            )}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {isOnBreak && (
            <div className="mb-4 rounded-[3px] border-l-[3px] border-orange-500 bg-orange-50 p-4">
              <p className="text-sm font-semibold text-orange-900">Currently on break:</p>
              <p className="text-sm text-orange-700">{userStatus.text}</p>
              <button
                onClick={handleEndBreak}
                className="mt-3 w-full rounded-[3px] bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                End Break & Return to Available
              </button>
            </div>
          )}

          <div className="space-y-2">
            {breakCodeOptions.length === 0 && (
              <p className="py-4 text-center text-sm text-slate-500">No break codes available</p>
            )}

            {breakCodeOptions.map((code) => (
              <button
                key={code.key}
                onClick={() => handleSelectBreak(code)}
                disabled={code.disabled}
                className={cn(
                  'w-full rounded-[3px] border border-slate-200 p-4 text-left transition-all',
                  code.disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:border-[#3366FF] hover:bg-blue-50',
                  userStatus.key === code.key && 'border-orange-500 bg-orange-50'
                )}
              >
                <div className="font-semibold text-slate-900">{code.text}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
